import React from "react";
import { motion } from "framer-motion";
import { Star, Quote } from "lucide-react";

const testimonials = [
  {
    name: "Ayşe Demir",
    role: "Düğün Organizatörü",
    company: "Dream Events",
    image: "👰",
    rating: 5,
    text: "Eyay Booth düğün organizasyonlarımızda vazgeçilmez oldu! Misafirler saatlerce sıra bekliyor ve muhteşem fotoğraflarla mutlu ayrılıyorlar. AI teknolojisi gerçekten etkileyici!",
    gradient: "from-purple-500 to-pink-500",
  },
  {
    name: "Mehmet Yılmaz",
    role: "Kurumsal Etkinlik Müdürü",
    company: "Tech Summit 2024",
    image: "💼",
    rating: 5,
    text: "2000 kişilik teknoloji zirvemizde kullandık. Hem eğlenceli hem profesyonel görünüyor. Katılımcılardan harika geri dönüşler aldık. Kesinlikle tavsiye ediyorum!",
    gradient: "from-cyan-500 to-blue-500",
  },
  {
    name: "Zeynep Kaya",
    role: "Doğum Günü Organizasyonu",
    company: "Kids Party Heaven",
    image: "🎂",
    rating: 5,
    text: "Çocukların doğum günü partilerinde kullanıyoruz. Süper kahraman ve prenses temalı fotoğraflar çocukları çok mutlu ediyor. Kurulumu çok kolay, kullanımı pratik!",
    gradient: "from-pink-500 to-rose-500",
  },
  {
    name: "Can Öztürk",
    role: "Festival Organizatörü",
    company: "Summer Fest",
    image: "🎪",
    rating: 5,
    text: "Festival alanımızda en çok ziyaret edilen nokta oldu! AI fotoğraflar sosyal medyada viral oldu ve festivalimizin tanıtımına büyük katkı sağladı. Mükemmel yatırım!",
    gradient: "from-orange-500 to-red-500",
  },
  {
    name: "Elif Arslan",
    role: "Marka Yöneticisi",
    company: "Fashion Week Istanbul",
    image: "👗",
    rating: 5,
    text: "Moda etkinliğimizde kullandık ve influencerlar bayıldı! Özelleştirilebilir temalar sayesinde markamızın kimliğini yansıttık. Profesyonel ve etkileyici!",
    gradient: "from-purple-500 to-indigo-500",
  },
  {
    name: "Burak Şahin",
    role: "Etkinlik Planlayıcısı",
    company: "Corporate Solutions",
    image: "🎯",
    rating: 5,
    text: "Kurumsal müşterilerimiz için ideal çözüm. Teknik destek mükemmel, kurulum hızlı. Etkinliklerimizin değerini katladı. Her etkinlikte mutlaka kullanıyoruz!",
    gradient: "from-green-500 to-emerald-500",
  },
];

export function Testimonials() {
  return (
    <section className="py-20 px-4 relative overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-900/10 to-transparent"></div>

      <div className="container mx-auto max-w-7xl relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-purple-400 via-pink-400 to-cyan-400 bg-clip-text text-transparent">
            Müşterilerimiz Ne Diyor?
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Binlerce mutlu müşterimizden sadece birkaçının deneyimleri
          </p>
        </motion.div>

        {/* Testimonials Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              whileHover={{ y: -10 }}
              className="group relative"
            >
              <div className="relative bg-gray-900/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-800 hover:border-purple-500/50 transition-all duration-300 h-full flex flex-col">
                {/* Quote Icon */}
                <div
                  className={`absolute -top-4 -left-4 w-12 h-12 rounded-full bg-gradient-to-r ${testimonial.gradient} flex items-center justify-center shadow-lg`}
                >
                  <Quote className="w-6 h-6 text-white" />
                </div>

                {/* Rating */}
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star
                      key={i}
                      className="w-5 h-5 fill-yellow-400 text-yellow-400"
                    />
                  ))}
                </div>

                {/* Testimonial Text */}
                <p className="text-gray-300 leading-relaxed mb-6 flex-grow">
                  "{testimonial.text}"
                </p>

                {/* Author Info */}
                <div className="flex items-center gap-4 pt-6 border-t border-gray-800">
                  <div
                    className={`w-12 h-12 rounded-full bg-gradient-to-r ${testimonial.gradient} flex items-center justify-center text-2xl`}
                  >
                    {testimonial.image}
                  </div>
                  <div>
                    <div className="text-white font-semibold">
                      {testimonial.name}
                    </div>
                    <div className="text-gray-400 text-sm">
                      {testimonial.role}
                    </div>
                    <div className="text-gray-500 text-xs">
                      {testimonial.company}
                    </div>
                  </div>
                </div>

                {/* Hover Glow Effect */}
                <div
                  className={`absolute inset-0 rounded-2xl bg-gradient-to-r ${testimonial.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-300`}
                ></div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center mt-16"
        >
          <div className="inline-flex items-center gap-3 bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-full px-6 py-3">
            <div className="flex -space-x-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center text-sm">
                👤
              </div>
              <div className="w-8 h-8 rounded-full bg-gradient-to-r from-pink-500 to-rose-500 flex items-center justify-center text-sm">
                👤
              </div>
              <div className="w-8 h-8 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center text-sm">
                👤
              </div>
            </div>
            <span className="text-gray-300">
              <span className="font-bold text-purple-400">10,000+</span> mutlu
              müşteri ile başarı hikayemizi büyütüyoruz
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
